import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ProductionCardBundle } from '@app/models/production/production-card/production-card-bundle';
import { ProductionCardBundleProxyService } from '@app/proxies/production/production-card-bundle.proxy.service';
import { IndividualProductionCardService } from './individual-production-card.service';

@Injectable({
  providedIn: 'root',
})
export class ProductionCardBundleService {
  private currentBundle: ProductionCardBundle | null = null;
  private _pending = signal(0);
  readonly isLoading = computed(() => this._pending() > 0);

  private readonly translate = inject(TranslateService);

  constructor(
    private readonly individualProductionCardService: IndividualProductionCardService,
    private readonly productionCardBundleProxyService: ProductionCardBundleProxyService,
  ) {}

  get productionCardBundles(): ProductionCardBundle[] {
    return this.individualProductionCardService.productionCardBundles;
  }

  addRollToBundle(rollNumber: string): void {
    const selectedBundle = this.getSelectedBundle();
    if (!selectedBundle) {
      console.error('No bundle selected to add roll to.');
      return;
    }

    this._pending.update((n) => n + 1);
    this.productionCardBundleProxyService.rollAction({ bundleReference: selectedBundle.id, rollNumber }).subscribe({
      next: (response) => {
        this._pending.update((n) => n - 1);
        if (!response.success) {
          return;
        }

        const updatedBundle = response.data;

        this.individualProductionCardService.updateProductionCardBundle(updatedBundle!);
        this.currentBundle = updatedBundle!;
        GlobalMessageUtil.success(this.translate.instant('Roll_Updated'), this.translate.instant('Roll_Updated_In_Bundle', { rollNumber }));
      },
      error: (error) => {
        this._pending.update((n) => n - 1);
        console.error('Error adding roll to bundle:', error);
      },
    });
  }

  selectBundle(bundleReference: string): Observable<boolean> {
    return this.productionCardBundleProxyService.getByBundleId(bundleReference).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          console.error('Failed to fetch bundle by ID');
          return false;
        }
        this.currentBundle = response.data;
        this.individualProductionCardService.updateProductionCardBundle(response.data);
        return true;
      }),
    );
  }

  getSelectedBundle(): ProductionCardBundle | null {
    return this.currentBundle;
  }

  changeStatus(newStatus: number): void {
    const currentBundle = this.currentBundle;
    if (_.isNil(currentBundle)) {
      console.error('No bundle selected to change status.');
      return;
    }

    this.productionCardBundleProxyService.changeStatus(currentBundle.id!, newStatus).subscribe({
      next: (response) => {
        if (!response.success) {
          return;
        }
        currentBundle.status = newStatus;
        this.individualProductionCardService.updateProductionCardBundle(currentBundle);
      },
    });
  }

  createBundles(): void {
    this._pending.update((n) => n + 1);
    this.productionCardBundleProxyService.createBundles(this.individualProductionCardService.getProductionCardOrThrow().id!).subscribe({
      next: (response) => {
        this._pending.update((n) => n - 1);
        if (!response.success) {
          return;
        }

        const newBundles = response.data;

        this.individualProductionCardService.addNewBundles(newBundles!);
        GlobalMessageUtil.success(
          this.translate.instant('Bundles_Created'),
          this.translate.instant('Bundles_Created_Successfully', { count: newBundles?.length ?? 0 }),
        );
      },
      error: (error) => {
        this._pending.update((n) => n - 1);
        console.error('Error creating bundles:', error);
      },
    });
  }
}
